import axios from 'axios';
import notificationService from '@motionelements/core/src/services/notification.service.js';
import { getJobStatus } from '@motionelements/core/src/api/jobs.js';
import { convertDownload, getDownloadConvertedVariants, deleteConvertedFile } from '@motionelements/core/src/api/downloads.js';
import i18n from '../../../lang/i18n.js';

function refreshCancelToken(state, tokenName) {
  // cancel previous request
  if (_.has(state.cancelTokens, tokenName)) {
    state.cancelTokens[tokenName].cancel();
  }

  // create cancel token
  state.cancelTokens[tokenName] = axios.CancelToken.source();

  return state.cancelTokens[tokenName];
}

const state = {
  download: null,
  job: null,
  n: 0,
  convertedVariants: [],
  //
  cancelTokens: {},
  //
  convertingVariants: [],
};

const getters = {
  jobStatus: state => _.get(state.job, 'status', ''),
  videoCodecCode() {
    return (codecs) => _.get(codecs.filter(codec => codec.type === 'video'), '[0].code');
  },
  convertedVariantsVideoCodes(state, getters) {
    return state.convertedVariants.map(v => getters.videoCodecCode(v.codecs));
  },
  convertingVariantsVideoCodes(state, getters) {
    return state.convertingVariants.map(v => getters.videoCodecCode(v.codecs));
  },
  currentDownloadConvertingVariants(state, getters) {
    return state.convertingVariants
      .filter(x => x.downloadId === _.get(state, 'download.id'))
      .filter(x => x.advanced || !getters.convertedVariantsVideoCodes.includes(getters.videoCodecCode(x.codecs)));
  },
  currentDownloadConvertingVariantsVideoCodes(state, getters) {
    return getters.currentDownloadConvertingVariants
      .filter(x => x.downloadId === _.get(state, 'download.id'))
      .map(x => getters.videoCodecCode(x.codecs));
  },
};

const actions = {
  convert({ commit, dispatch, state }, { downloadId, payload }) {
    const cancelToken = refreshCancelToken(state, 'convertDownload');
    const axiosConfig = { cancelToken: cancelToken.token };
    commit('setJob', { status: 'running' });
    return convertDownload({ downloadId, payload, axiosConfig })
      .then((response) => {
        const job = response.data.data;
        commit('setJob', { ...state.job, ...job });
        dispatch('getJobStatus', job.id);
        return job;
      })
      .catch(err => {
        console.error(err);
        dispatch('handleConvertingFailed', { err });
      });
  },
  getJobStatus({ commit, dispatch, state }, jobId) {
    const cancelToken = refreshCancelToken(state, 'getJobStatus');
    const axiosConfig = { cancelToken: cancelToken.token };
    commit('increaseCount');
    return getJobStatus(jobId, axiosConfig)
      .then((response) => {
        console.log(state.n);
        const job = response.data.data;
        // commit('setJob', job);
        if (job.status === 'done') {
          dispatch('handleConvertingSuccessfull', job);
        }
        if (!['done', 'failed'].includes(job.status)) {
          dispatch('handleConvertingStillRunning', job);
        }
        if (job.status === 'failed') {
          dispatch('handleConvertingFailed', { jobId: job.id });
        }
      })
      .catch(err => {
        console.error(err);
        if (!axios.isCancel(err)) dispatch('handleConvertingFailed', { jobId, err });
      });
  },
  getConvertedVariants({ commit }, { downloadSku, orderId }) {
    return getDownloadConvertedVariants(downloadSku, orderId)
      .then(response => {
        // response.data.data[0].error = 'Error';
        // response.data.data[0].downloading = true;
        commit('setConvertedVariants', response.data.data);
      });
  },
  handleConvertingFailed({ commit }, { jobId, err }) {
    commit('setJob', { status: 'failed' });
    const errorText = _.get(err, 'response.data.messages[0].title', i18n.t('download.convert.failed_text'));
    if (jobId) commit('setConvertingVariantFailed', { jobId, error: errorText });
    notificationService.alert({
      level: 'danger',
      title: errorText,
      autohide: true,
    });
  },
  handleConvertingSuccessfull({ commit, dispatch, state }, job) {
    commit('removeConvertingVariant', job.id);
    commit('reset');
    notificationService.alert({
      level: 'success',
      title: i18n.t('download.convert.success_text'),
    });
    if (state.download) {
      dispatch('getConvertedVariants', {
        downloadSku: state.download.sku,
        orderId: state.download.order.id,
      })
        .finally(() => {
          commit('setJob', job);
        });
    }
  },
  handleConvertingStillRunning({ commit, dispatch, state }, job) {
    commit('setJob', job);
    if (state.n > 1) {
      setTimeout(() => {
        dispatch('getJobStatus', job.id);
      }, 1000);
    } else {
      dispatch('getJobStatus', job.id);
    }
  },
  deleteConvertedVariant({ state }, fileId) {
    const cancelToken = refreshCancelToken(state, `deleteConvertedFile-${fileId}`);
    const axiosConfig = { cancelToken: cancelToken.token };
    return deleteConvertedFile(fileId, axiosConfig);
  },
};

const mutations = {
  setJob(state, job) {
    state.job = job;
  },
  setDownload(state, download) {
    state.download = download;
  },
  setConvertedVariants(state, convertedVariants) {
    state.convertedVariants = convertedVariants;
  },
  increaseCount(state) {
    state.n += 1;
  },
  reset(state) {
    state.job = null;
    state.n = 0;
  },
  pushConvertingVariant(state, variant) {
    const index = state.convertingVariants.findIndex(x => x.jobId === variant.jobId);
    if (index < 0) {
      state.convertingVariants.unshift(variant);
    } else {
      state.convertingVariants.splice(index, 1);
    }
  },
  pushConvertedVariant(state, variant) {
    const index = state.convertedVariants.findIndex(x => _.get(x, 'file.id') === _.get(variant, 'file.id'));
    if (index < 0) state.convertedVariants.push(variant);
  },
  removeConvertingVariant(state, jobId) {
    const index = state.convertingVariants.findIndex(x => x.jobId === jobId);
    if (index > -1) state.convertingVariants.splice(index, 1);
  },
  removeConvertedVariant(state, fileId) {
    const index = state.convertedVariants.findIndex(x => _.get(x, 'file.id') === fileId);
    if (index > -1) state.convertedVariants.splice(index, 1);
  },
  setConvertingVariantFailed(state, { jobId, error }) {
    const targetVariant = state.convertingVariants.find(x => x.jobId === jobId);
    if (targetVariant) {
      targetVariant.error = error;
      targetVariant.file.status = 'failed';
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
